import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Text } from 'grommet';
import { formatDateShort } from '@helpers/utils/dateUtils';
import { FormDown, FormUp } from 'grommet-icons';
import axios from '../../helpers/axios';
import PlayerClip from './PlayerClip';
import { ClipFeedClip, PlayerGameClipsContainerProps } from './player.types';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';

const PlayerGameClipsFeed: React.FC<
  PlayerGameClipsContainerProps & RouteComponentProps
> = ({
  playerId,
  game,
  role,
  isAdmin,
  pinnedClips,
  pinClip,
  unpinClip,
  onShowClaim,
  showClips: initialShowClips = false,
  history,
  publicPublishLevel = 10,
  location,
}) => {
  const activeClipRef = useRef<HTMLDivElement>(null);
  const firstClipRef = useRef<HTMLDivElement>(null);

  const [showClips, setShowClips] = useState(initialShowClips);
  const [loadingClips, setLoadingClips] = useState(false);
  const [clips, setClips] = useState<ClipFeedClip[]>([]);
  const [localPinnedClips, setLocalPinnedClips] =
    useState<ClipFeedClip[]>(pinnedClips);
  const [localRole, setLocalRole] = useState(role); // Track local role to detect changes

  // Update localPinnedClips whenever pinnedClips prop changes
  useEffect(() => {
    setLocalPinnedClips(pinnedClips);
  }, [pinnedClips]);

  // Fetch clips based on game ID and role
  const fetchClips = async () => {
    try {
      setLoadingClips(true);
      const response = await axios.get(
        `/api/player/${playerId}/game/${game.id}/clips`
      );
      const filteredClips = response.data.clips.filter(
        (clip: ClipFeedClip) => clip.playerRole === role
      );
      setClips(filteredClips);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading clips:', error);
    } finally {
      setLoadingClips(false);
    }
  };

  // Handle hash-based navigation
  useEffect(() => {
    if (location.hash) {
      const [gameId, hashRole] = location.hash.substring(1).split('-');
      if (gameId === game.id && hashRole === role) {
        setShowClips(true);
        fetchClips(); // Fetch clips for the hash-based game and role
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, game.id, role]);

  // Handle role changes (when switching between tabs)
  useEffect(() => {
    if (localRole !== role) {
      setShowClips(false); // Close the clips dropdown
      setClips([]); // Clear clips to ensure fresh loading for the new role
      setLocalRole(role); // Update the local role
    }
  }, [role, localRole]);

  // Load clips when showClips is true
  useEffect(() => {
    if (showClips && clips.length === 0) {
      fetchClips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClips, clips.length]);

  // Scroll to the first clip when clips are shown
  useEffect(() => {
    if (showClips && clips.length > 0 && firstClipRef.current) {
      // @note: Adding for PlayerPageFirst A/B test. Should be removed after the test.
      // NKS 2025-06-03
      const searchParams = new URLSearchParams(location.search.toLowerCase());
      const isPlayerPageFirstGroup =
        searchParams.get('playerpagefirst') !== null;

      if (isPlayerPageFirstGroup) {
        document.getElementById(`${game.id}-${role}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        firstClipRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [showClips, clips, firstClipRef, location.search, game.id, role]);

  // Get the reference for the active clip
  const getRef = (clip: ClipFeedClip) => {
    return activeClipRef.current &&
      (clip.refFull === activeClipRef.current.id ||
        clip.refPreview === activeClipRef.current.id)
      ? activeClipRef
      : null;
  };

  // Toggle icon for showing/hiding clips
  const toggleIcon = showClips ? (
    <FormUp size="small" color="secondary-1" />
  ) : (
    <FormDown size="small" color="secondary-1" />
  );

  return (
    <Box id={`${game.id}-${role}`}>
      <Card
        key={game.id}
        gap="medium"
        background="secondary-7"
        round="small"
        onClick={() => setShowClips(!showClips)}
      >
        <Box direction="row" justify="between" pad="medium" gap="medium">
          <Box gap="medium">
            <Box direction="row" gap="medium" align="center">
              <Text size="large" weight="bold">
                vs {game.scoreOpponentName}
              </Text>
            </Box>
            <Text size="medium">{formatDateShort(game.scoreTsStart)}</Text>
          </Box>
          <Box gap="small" alignSelf="end">
            <Button
              primary
              color="primary-1"
              label="Play-by-Play"
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/game/${game.id}`);
              }}
            />
            <Button
              className="button-my-clips"
              label="My Clips"
              icon={toggleIcon}
              reverse
              onClick={() => setShowClips(!showClips)}
            />
          </Box>
        </Box>
      </Card>
      {showClips && (
        <Box fill="horizontal">
          {loadingClips && (
            <Box justify="center" align="center" pad="medium">
              <MoonLoader size={30} color="#024006" />
            </Box>
          )}
          {clips.length === 0 && !loadingClips ? (
            <Text>No clips available for this role in the selected game.</Text>
          ) : (
            clips.map((clip, index) => {
              const isHidden =
                clip.O_hideFromClaimedPlayerPage?.includes(playerId);
              const shouldShow = !isHidden || isAdmin;

              if (!shouldShow) {
                return null;
              }

              return (
                <PlayerClip
                  key={clip.id}
                  game={game}
                  clip={clip}
                  active={false}
                  playerId={playerId}
                  pinClip={isAdmin ? pinClip : null}
                  unpinClip={isAdmin ? unpinClip : null}
                  isPinned={localPinnedClips.some(
                    (c) => c.id === clip.refPreview || c.id === clip.refFull
                  )}
                  onShowClaim={onShowClaim}
                  hideBottomBar={publicPublishLevel < 10}
                  ref={index === 0 ? firstClipRef : getRef(clip)}
                  publicPublishLevel={
                    clip.J_claimedPlayerList.length > 0 || index === 0
                      ? publicPublishLevel
                      : -1
                  }
                />
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

export default withRouter(PlayerGameClipsFeed);
